import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Icon,
  Input,
  Modal,
  NumberInput,
  Popconfirm,
  Space,
  Spin,
  Table,
  Tag,
  Text,
  useToast,
} from "@app/ui";
import { ErrorAlert, useErrorText } from "../errors/index.js";
import { createApiKey, listApiKeys, revokeApiKey } from "../api/dpp.js";
import { useT, useFormatDateTime } from "../i18n.js";
import { API_KEY_SCOPES, type ApiKey, type ApiKeyScope, type ApiKeyStatus } from "../types.js";

/**
 * "API keys" card of the organization settings page (machine integration for
 * ERP connectors). Create modal with scope checkboxes, one-time raw-key
 * display, revoke. Rendered only for holders of `integration:manage` (admin)
 * — mirrors AccessGrantsCard.
 *
 * The raw dpk_ key exists ONLY in the create response — the API stores just
 * its hash. It is surfaced once in the modal and never retrievable again.
 */

const STATUS_COLOR: Record<ApiKeyStatus, "default" | "success" | "error"> = {
  active: "success",
  revoked: "default",
  expired: "error",
};

export function ApiKeysCard({ orgId }: { orgId: string }) {
  const t = useT();
  const fmtDate = useFormatDateTime();
  const toast = useToast();
  const errorText = useErrorText();

  const [keys, setKeys] = useState<ApiKey[] | null>(null);
  const [error, setError] = useState<unknown>(null);

  // Create-key modal
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState<ApiKeyScope[]>(["products:read"]);
  const [expiresDays, setExpiresDays] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<unknown>(null);
  const [createdToken, setCreatedToken] = useState<string | null>(null);

  const load = async () => {
    setError(null);
    try {
      setKeys(await listApiKeys(orgId));
    } catch (e) {
      setError(e);
    }
  };

  useEffect(() => {
    void load();
  }, [orgId]);

  const copy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(t("apiKeys.copied"));
    } catch {
      toast.error(t("error.actionFailed"));
    }
  };

  const openCreate = () => {
    setName("");
    setScopes(["products:read"]);
    setExpiresDays("");
    setCreateError(null);
    setCreatedToken(null);
    setCreateOpen(true);
  };

  const toggleScope = (scope: ApiKeyScope, checked: boolean) => {
    setScopes((prev) => (checked ? [...new Set([...prev, scope])] : prev.filter((s) => s !== scope)));
  };

  const submitCreate = async () => {
    if (name.trim() === "") {
      setCreateError(t("apiKeys.nameRequired"));
      return;
    }
    if (scopes.length === 0) {
      setCreateError(t("apiKeys.scopeRequired"));
      return;
    }
    setCreateError(null);
    setCreating(true);
    try {
      const days = Number(expiresDays);
      const result = await createApiKey(orgId, {
        name: name.trim(),
        scopes,
        ...(Number.isFinite(days) && days > 0 ? { expiresInDays: days } : {}),
      });
      // The raw key is only ever available HERE — show it once.
      setCreatedToken(result.token);
      toast.success(t("apiKeys.created"));
      await load();
    } catch (e) {
      setCreateError(e);
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (keyId: string) => {
    try {
      await revokeApiKey(orgId, keyId);
      toast.success(t("apiKeys.revoked"));
      await load();
    } catch (e) {
      toast.error(errorText(e));
    }
  };

  return (
    <Card
      title={t("apiKeys.title")}
      extra={
        <Button size="sm" icon={<Icon name="plus" size={14} />} onClick={openCreate}>
          {t("apiKeys.create")}
        </Button>
      }
    >
      {error != null && <ErrorAlert error={error} />}
      {keys === null ? (
        <Spin />
      ) : (
        <Table
          rowKey={(k) => k.id}
          empty={t("apiKeys.empty")}
          columns={[
            { key: "name", title: t("apiKeys.name"), render: (k: ApiKey) => k.name },
            {
              key: "prefix",
              title: t("apiKeys.keyPrefix"),
              render: (k: ApiKey) => <Text code>{k.keyPrefix}…</Text>,
            },
            {
              key: "scopes",
              title: t("apiKeys.scopes"),
              render: (k: ApiKey) => (
                <Space size="sm">
                  {k.scopes.map((s) => (
                    <Tag key={s}>{s}</Tag>
                  ))}
                </Space>
              ),
            },
            {
              key: "status",
              title: t("common.status"),
              render: (k: ApiKey) => (
                <Tag color={STATUS_COLOR[k.status]}>{t(`apiKeyStatus.${k.status}`)}</Tag>
              ),
            },
            {
              key: "lastUsedAt",
              title: t("apiKeys.lastUsed"),
              render: (k: ApiKey) => (k.lastUsedAt ? fmtDate(k.lastUsedAt) : "—"),
            },
            {
              key: "expiresAt",
              title: t("apiKeys.expiry"),
              render: (k: ApiKey) => (k.expiresAt ? fmtDate(k.expiresAt) : t("apiKeys.noExpiry")),
            },
            {
              key: "actions",
              title: t("common.actions"),
              render: (k: ApiKey) =>
                k.status !== "revoked" ? (
                  <Popconfirm
                    title={t("apiKeys.revokeConfirm")}
                    description={t("apiKeys.revokeDescription")}
                    okType="danger"
                    okText={t("apiKeys.revoke")}
                    cancelText={t("common.cancel")}
                    onConfirm={() => void revoke(k.id)}
                  >
                    <Button size="sm" variant="danger">
                      {t("apiKeys.revoke")}
                    </Button>
                  </Popconfirm>
                ) : null,
            },
          ]}
          data={keys}
        />
      )}

      {/* Create key */}
      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title={createdToken ? t("apiKeys.tokenTitle") : t("apiKeys.createTitle")}
        footer={createdToken ? null : undefined}
        okText={t("common.create")}
        cancelText={t("common.cancel")}
        onOk={() => void submitCreate()}
        confirmLoading={creating}
      >
        {createdToken ? (
          <Space direction="vertical" size="md" style={{ width: "100%" }}>
            <Alert type="warning">{t("apiKeys.tokenOnce")}</Alert>
            <Input readOnly value={createdToken} onFocus={(e) => e.target.select()} />
            <Space size="sm">
              <Button
                variant="primary"
                icon={<Icon name="copy" size={14} />}
                onClick={() => void copy(createdToken)}
              >
                {t("apiKeys.copy")}
              </Button>
              <Button onClick={() => setCreateOpen(false)}>{t("common.cancel")}</Button>
            </Space>
          </Space>
        ) : (
          <Space direction="vertical" size="md" style={{ width: "100%" }}>
            {createError != null && <ErrorAlert error={createError} />}
            <Text small secondary>
              {t("apiKeys.intro")}
            </Text>
            <Input
              label={t("apiKeys.name")}
              help={t("apiKeys.nameHelp")}
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <Space direction="vertical" size="sm">
              <Text small>{t("apiKeys.scopes")}</Text>
              {API_KEY_SCOPES.map((scope) => (
                <Checkbox
                  key={scope}
                  checked={scopes.includes(scope)}
                  onChange={(e) => toggleScope(scope, e.target.checked)}
                >
                  <Text code>{scope}</Text>
                </Checkbox>
              ))}
            </Space>
            <NumberInput
              label={t("apiKeys.expiresInDays")}
              help={t("apiKeys.expiresHelp")}
              min={1}
              max={3650}
              decimals={0}
              value={expiresDays}
              onChange={setExpiresDays}
            />
          </Space>
        )}
      </Modal>
    </Card>
  );
}
